import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CatIconBatteryFull } from "blocks-nextjs/blocks-react/components/Icons/BatteryFull/BatteryFull";
import { CatIconBatteryLow } from "blocks-nextjs/blocks-react/components/Icons/BatteryLow/BatteryLow";
import * as d3 from "d3";
import React, { useEffect, useRef } from "react";
import { getCESTLocalTime } from "../../pages/Projects/ProjectDetails.helper";
import { getTimezoneAbbr } from "../../shared/helper";
const ChargerTimeline = ({ hasMore = false, isLoadingMore = false, onLoadMore, timezone, transactions = [] }) => {
    const svgRef = useRef(null);
    const dataToShow = transactions;
    const svgHeight = Math.max(100 * dataToShow.length, 200);
    // Format timestamp using the shared helper function from ProjectDetails.helper.ts
    // This ensures consistent timezone display across the entire application
    // The helper already handles timezone formatting correctly without conversion
    const formatTimestamp = (timestamp) => {
        if (!timestamp)
            return "N/A";
        // getCESTLocalTime handles the Z-appending approach for display consistency
        // Returns formatted date or null if invalid
        const formatted = getCESTLocalTime(timestamp, "MM/dd/yyyy; HH:mm");
        if (!formatted)
            return "N/A";
        // Use shared getTimezoneAbbr helper which handles DST and various timezone formats
        // This is consistent with how timezones are displayed throughout the application
        const timezoneAbbr = timezone ? getTimezoneAbbr(timezone, timestamp) : "";
        // Append timezone abbreviation if available
        return timezoneAbbr ? `${formatted} ${timezoneAbbr}` : formatted;
    };
    useEffect(() => {
        if (!svgRef.current)
            return;
        const svg = d3.select(svgRef.current);
        const margin = { bottom: 20, left: 20, right: 120, top: 40 };
        svg.selectAll("*").remove();
        const yScale = d3
            .scalePoint()
            .domain(d3.range(dataToShow.length).map(String))
            .range([margin.top, svgHeight - margin.bottom]);
        // Draw vertical timeline line
        svg.append("line")
            .attr("x1", margin.left)
            .attr("y1", margin.top)
            .attr("x2", margin.left)
            .attr("y2", svgHeight - margin.bottom)
            .attr("stroke", "#000")
            .attr("stroke-width", 2);
        // Render timeline circles
        dataToShow.forEach((item, index) => {
            const yPosition = yScale(String(index)) || 0;
            // Add circle marker
            svg.append("circle")
                .attr("cx", margin.left)
                .attr("cy", yPosition)
                .attr("r", 6)
                .attr("fill", index === 0 ? "#000" : "#FFF")
                .attr("stroke", "#000")
                .attr("stroke-width", 2);
        });
    }, [dataToShow, svgHeight]);
    const formatTransactionDisplay = (transaction) => {
        const startSoc = transaction.initialSoc?.value !== undefined ? `${transaction.initialSoc.value}%` : "N/A";
        const endSoc = transaction.finalSoc?.value !== undefined ? `${transaction.finalSoc.value}%` : "N/A";
        const energy = transaction.energyDelivered
            ? `${transaction.energyDelivered.value} ${transaction.energyDelivered.unitOfMeasure}`
            : "N/A";
        const startSocValue = Number(transaction.initialSoc?.value) || 0;
        const endSocValue = Number(transaction.finalSoc?.value) || 0;
        const startIcon = startSocValue <= 20 ? _jsx(CatIconBatteryLow, {}) : _jsx(CatIconBatteryFull, {});
        const endIcon = endSocValue <= 20 ? _jsx(CatIconBatteryLow, {}) : _jsx(CatIconBatteryFull, {});
        // Calculate duration between startTime and endTime
        let duration = "N/A";
        if (transaction.startTime && transaction.endTime) {
            const startMs = new Date(transaction.startTime).getTime();
            const endMs = new Date(transaction.endTime).getTime();
            const durationMs = endMs - startMs;
            if (durationMs > 0) {
                const hours = Math.floor(durationMs / (1000 * 60 * 60));
                const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
                if (hours > 0 && minutes > 0) {
                    duration = `${hours} hr ${minutes} min`;
                }
                else if (hours > 0) {
                    duration = `${hours} hr`;
                }
                else if (minutes > 0) {
                    duration = `${minutes} min`;
                }
                else {
                    duration = "< 1 min";
                }
            }
        }
        return { duration, endIcon, endSoc, energy, startIcon, startSoc };
    };
    return (_jsxs("div", { className: "w-full", children: [_jsxs("div", { className: "flex justify-between items-center mr-4 [w-93%]", children: [_jsx("div", {}), _jsx("span", { className: "text-sm text-gray-500", children: "Start Time" })] }), _jsxs("div", { className: "w-full h-[320px] bg-white relative", children: [_jsx("div", { className: "h-full overflow-y-auto pl-4 pr-2 pt-0 pb-12", children: _jsx("div", { className: "flex", children: _jsx("div", { className: "flex-1 mt-2", children: dataToShow.map((transaction, index) => {
                                    const { duration, endIcon, endSoc, energy, startIcon, startSoc } = formatTransactionDisplay(transaction);
                                    const startTime = formatTimestamp(transaction.startTime);
                                    return (_jsxs("div", { className: `flex ${index === 0 ? "mt-0" : ""}`, children: [_jsxs("div", { className: "flex flex-col items-center mr-4 flex-shrink-0 self-stretch", children: [_jsx("div", { className: `w-3 h-3 rounded-full border-2 border-black flex-shrink-0 ${index === 0 ? "bg-black" : "bg-white"}` }), index < dataToShow.length - 1 && (_jsx("div", { className: "w-0.5 flex-1 bg-black" }))] }), _jsxs("div", { className: "flex justify-between items-start flex-1 pb-12 pt-0", children: [_jsxs("div", { className: "flex flex-col space-y-1 flex-1 mt-[-5px]", children: [_jsx("div", { className: "text-sm font-semibold", children: "Unknown Machine | Unknown Serial number" }), _jsxs("div", { className: "text-sm text-gray-600", children: [duration, " | ", energy] }), _jsxs("div", { className: "flex items-center gap-2 text-sm text-gray-600", children: [_jsxs("span", { className: "flex items-center gap-1", children: [startIcon, startSoc] }), _jsx("span", { children: "\u2192" }), _jsxs("span", { className: "flex items-center gap-1", children: [endIcon, endSoc] })] })] }), _jsxs("div", { className: "text-right flex-shrink-0 ml-4", children: [_jsx("div", { className: "text-sm text-gray-500", children: startTime.split(";")[0] }), _jsx("div", { className: "text-sm text-gray-500", children: startTime.split(";")[1]?.trim() })] })] })] }, transaction.transactionId || index));
                                }) }) }) }), hasMore && (_jsx("div", { className: "absolute bottom-0 left-0 right-0 bg-white flex justify-center py-2", children: _jsx("button", { className: "text-blue-600 text-sm hover:underline disabled:text-gray-400 disabled:cursor-not-allowed", disabled: isLoadingMore, onClick: onLoadMore, children: isLoadingMore ? "Loading..." : "Show More" }) }))] })] }));
};
export default ChargerTimeline;
